import React from 'react';

import { faEdit, faSpinnerThird, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    useUpdateSpecificPurposeAssignment,
    type UpdateSpecificPurposeAssignmentFormSchemaValues,
    updateSpecificPurposeAssignmentFormSchema,
} from 'src/features/pricingPlans/api/useUpdateSpecificPurposeAssigment';
import { ASSIGMENT_CODE, type PricingPlan } from 'src/features/pricingPlans/types';
import { pricingPlanNamesMessages, pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage, Form } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { ListResponseSmartParkAPI } from 'src/types';

interface AssignmentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAssignment: { code: ASSIGMENT_CODE; planId: number | null };
    pricingPlans?: ListResponseSmartParkAPI<PricingPlan> | undefined;
    isDialogOpen?: boolean;
    onCloseDialog?: () => void;
    onSubmit?: () => void;
}

function AssignmentFormModal<TData>({
    selectedAssignment,
    isDialogOpen,
    onCloseDialog,
    pricingPlans,
    onSubmit: refetchAssignments,
}: AssignmentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [formPlanId, setFormPlanId] = React.useState<string>(
        selectedAssignment.planId ? selectedAssignment.planId.toString() : '',
    );
    const { formatMessage } = useIntl();
    const { parkingId, parkingRole } = useParking();
    const { mutate: updateAssignment, isPending: isUpdatePending } = useUpdateSpecificPurposeAssignment({
        onSuccess: () => {
            onOpenChange(false);
            if (refetchAssignments) refetchAssignments();
            toast({
                title: formatMessage(pricingPlansMessages.changePlanSubtitle, {
                    planName: formatMessage(pricingPlanNamesMessages[selectedAssignment.code]),
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { planId = null } = selectedAssignment || {};

    const form = useForm<UpdateSpecificPurposeAssignmentFormSchemaValues>({
        resolver: zodResolver(updateSpecificPurposeAssignmentFormSchema),
        defaultValues: {
            planId,
        },
    });

    React.useEffect(() => {
        form.reset(selectedAssignment);
    }, [selectedAssignment]);

    const onSubmit = (values: UpdateSpecificPurposeAssignmentFormSchemaValues) => {
        const submitData = {
            parkingId: parkingId,
            code: selectedAssignment.code,
            ...values,
        };
        return updateAssignment(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) setIsModalOpen(isOpen);
        if (!isOpen) {
            form.reset();
            if (onCloseDialog) onCloseDialog();
        }
    };
    if (parkingRole == MEMBERSHIP_ROLES.CLIENT) return <></>;
    return (
        <Dialog modal open={isModalOpen || isDialogOpen} onOpenChange={onOpenChange}>
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>{formatMessage(pricingPlansMessages.changePlan)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(pricingPlansMessages.changePlanSubtitle, {
                                    planName: formatMessage(
                                        pricingPlanNamesMessages[selectedAssignment.code],
                                    ).toLowerCase(),
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                        <FormField
                            control={form.control}
                            name="planId"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog">
                                        {formatMessage(pricingPlansMessages.timeToLeaveType)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <div className="flex gap-4">
                                                <Select
                                                    onValueChange={(value) => {
                                                        if (!value) return;
                                                        setFormPlanId(value);
                                                        field.onChange(Number(value));
                                                    }}
                                                    value={formPlanId}
                                                >
                                                    <SelectTrigger>
                                                        {formPlanId ? (
                                                            <SelectValue />
                                                        ) : (
                                                            <span className="text-gray-400">N/A</span>
                                                        )}
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {pricingPlans?.results.map((plan) => (
                                                            <SelectItem key={plan.id} value={plan.id.toString()}>
                                                                {plan.plan_name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {selectedAssignment.code === ASSIGMENT_CODE.LOST_TICKET && (
                                                    <Button
                                                        type="button"
                                                        variant="destructiveLight"
                                                        onClick={() => {
                                                            setFormPlanId('');
                                                            field.onChange(null);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                    </Button>
                                                )}
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={
                            !form.formState.isValid ||
                            isUpdatePending ||
                            selectedAssignment.planId === form.getValues().planId
                        }
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isUpdatePending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(pricingPlansMessages.changePlan)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { AssignmentFormModal };
